import { useMemo } from 'react';
import { isNil, omitBy } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { ActorTypeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type { ActorForm_Actor } from '../fragments/actor.fragment';

interface Params {
  readonly index: number;
  readonly isInternal: boolean;
  readonly initialValues?: ActorForm_Actor | null;
}

export const useInitialValuesFactory = ({
  index,
  isInternal,
  initialValues: pInitialValues,
}: Readonly<Params>): Readonly<{ initialValues: ActorForm_Actor }> => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultActorType } = ensureDefined({
    defaultActorType: referencesContext.defaultActorType,
  });

  // FIXME:  Typescript error
  const initialValues: ActorForm_Actor = useMemo(() => {
    let specificDefaultData;
    switch (defaultActorType.code) {
      case ActorTypeEnum.ActorOrganization: {
        specificDefaultData = {
          __typename: ActorTypeEnum.ActorOrganization as const,
          kind: ActorTypeEnum.ActorOrganization,
          typeId: defaultActorType._id,
          representatives: EMPTY_ARRAY,
          references: EMPTY_ARRAY,
          ...(isInternal ? { useItsEdmDomain: false } : EMPTY_OBJECT),
        };
        break;
      }
      case ActorTypeEnum.ActorPerson: {
        specificDefaultData = {
          __typename: ActorTypeEnum.ActorPerson as const,
          kind: ActorTypeEnum.ActorPerson,
          typeId: defaultActorType._id,
          references: EMPTY_ARRAY,
          ...(!isInternal ? { contextualRoleIds: EMPTY_ARRAY } : EMPTY_OBJECT),
        };
        break;
      }
      default: {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Unknown default actor type: ${defaultActorType.code}`);
      }
    }

    return {
      _id: generateId(),
      ...specificDefaultData,
      thirdPartyId: EMPTY_ID,
      categoryIds: EMPTY_ARRAY,
      ...omitBy(pInitialValues, isNil),
      index,
      isInternal,
    };
  }, [
    index,
    isInternal,
    generateId,
    pInitialValues,
    defaultActorType._id,
    defaultActorType.code,
  ]);

  return { initialValues };
};
